import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { DataService } from 'src/app/services/data/data.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { CpOspAssignments } from 'src/app/services/types/cp-osp-assignment.model';
import { Crew } from 'src/app/services/types/crew.model';
import { DailyReport } from 'src/app/services/types/daily-report.model';
import { OSP } from 'src/app/services/types/osp.model';
import { Task } from 'src/app/services/types/task.model';

@Component({
  selector: 'daily-importer',
  templateUrl: './daily-importer.page.html',
  styleUrls: ['./daily-importer.page.scss'],
  providers: [DataService, DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DailyImporterV21Page implements OnInit {
  guid: string = '';
  cpOspAssignmentLoaded$ = new BehaviorSubject<boolean>(false);

  data?: CpOspAssignments;

  osp: OSP;

  newDailyReports: DailyReport[] = [new DailyReport()];

  selectedDate: string;

  get minDate():string{
    let date = new Date();
    date.setDate(date.getDate() - 14);
    return date.toISOString();
  }

  get maxDate():string{
    let date = new Date();
    date.setDate(date.getDate() + 2);
    return date.toISOString();
  }

  get activeCrews():Crew[]{
    if(!this.data?.crews){
      return [];
    }

    return this.data.crews.filter(crew=>crew.active);
  }

  get canBeSaved(): boolean {
    const unsavedIndex = this.newDailyReports.findIndex((dr) => !dr.saved);

    if (unsavedIndex == -1) {
      return false;
    }

    return (
      this.newDailyReports[unsavedIndex].code &&
      this.newDailyReports[unsavedIndex].code.length != 0 &&
      (this.newDailyReports[unsavedIndex].qty || this.newDailyReports[unsavedIndex].qty == 0) &&
      this.newDailyReports[unsavedIndex].workDate &&
      this.newDailyReports[unsavedIndex].workDate.length != 0
    );
  }

  constructor(
    private dataService: DataService,
    private datePipe: DatePipe,
    private toastService: ToastService,
    private route: ActivatedRoute,
    private cd: ChangeDetectorRef
  ) {
    this.route.params.subscribe((params) => {
      if (params['guid']) {
        this.guid = params['guid'];
        if (!this.guid) {
          return;
        }

        this.getCpOSPData(this.guid);
      }
    });
  }

  ngOnInit() {}

  getCpOSPData(guid: string) {
    this.cpOspAssignmentLoaded$.next(false);
    this.dataService.getCpOspData(guid).subscribe((res: any) => {
      if (!res || res?.success == false) {
        this.toastService.presentToast(
          'Error',
          res?.error,
          'top',
          'danger',
          4000
        );
        return;
      }
      this.data = new CpOspAssignments(res);
      this.cpOspAssignmentLoaded$.next(true);
      this.cd.detectChanges();
    });
  }

  onWorkDateChange(event, i: number) {
    this.newDailyReports[i].workDate = this.datePipe.transform(
      event.detail.value,
      'yyyy-MM-dd'
    );
  }

  transformDate(date: string) {
    const dateString = this.datePipe.transform(date, 'MM-dd-yyyy');
    return dateString;
  }

  onTaskChange(event, i: number) {
    const selectedTask = event.value as Task;
    this.newDailyReports[i].code =
      selectedTask.relatedCPBillingCode?.toString();
    this.newDailyReports[i].task = selectedTask;
  }

  onCrewChange(event, i: number) {
    const selectedCrew = event.value as Crew;
    this.newDailyReports[i].crew = selectedCrew;
 }

  onAddDailyReportClick() {
    this.newDailyReports.push(new DailyReport());
    this.newDailyReports[this.newDailyReports.length-1].workDate= this.newDailyReports[this.newDailyReports.length -2].workDate;
    this.cd.markForCheck();
  }

  getDailyReportShortDescription(dr: DailyReport): string {
    if (dr.finalBillingCodeDescription) {
      return dr.finalBillingCodeDescription;
    }

    if (dr.description) {
      return dr.description;
    }

    if(dr.cpBillingCodeShortDescription){
      return dr.cpBillingCodeShortDescription
    }
    
    if (!this.data?.cpBillingCodes) {
      return '';
    }

    const bc = this.data?.cpBillingCodes.find(
      (bc) => bc.recordId == dr.cpBillingCodeId
    );
    
    if (bc) {
      return bc.billingCodeShortDescription;
    }

    return '';
  }

  onPostClick() {
    const unsavedIndex = this.newDailyReports.findIndex((dr) => !dr.saved);

    if (unsavedIndex == -1) {
      this.toastService.presentToast(
        'Error',
        'Please fill Daily Report',
        'top',
        'danger',
        4000
      );
      return;
    }

    this.dataService
      .postDailyReportV3(this.data.osp.recordId, {
        workDate: this.datePipe.transform(
          this.newDailyReports[unsavedIndex].workDate,
          'yyyy-MM-dd'
        ),
        selectedContractPartner:
          this.data.contractPartner.relatedContractPartner,
        relatedCPTask: this.newDailyReports[unsavedIndex].task.rid,
        selectedBillingCode:
          this.newDailyReports[unsavedIndex].task.relatedCPBillingCode,
        relatedCrew: this.newDailyReports[unsavedIndex].crew?.rid,
        quantity: this.newDailyReports[unsavedIndex].qty,
        notes: this.newDailyReports[unsavedIndex].description,
      })
      .subscribe((res) => {
        if (res['success']) {
          this.newDailyReports[unsavedIndex].saved = true;
          this.toastService.presentToast(
            'Success',
            'Daily Report Saved',
            'top',
            'success',
            4000
          );
          this.getCpOSPData(this.guid);
        }

        this.cd.markForCheck();
      });
  }

  getQuantityNumbers(task: Task, currentQty: number): string {
    if (!task || isNaN(task.planQty)) {
      return '';
    }

    let savedQty = this.data.dailyReports
      .filter((dr) => dr.code == task.CPBillingCode)
      .map((filteredDr) => filteredDr.qty)
      .reduce((partSum, a) => partSum + a, 0);

    if (!isNaN(currentQty)) savedQty += currentQty;

    return `${savedQty}/${task.planQty}`;
  }

  selectColor(task: Task, currentQty: number): string {
    if (!task || isNaN(task.planQty)) {
      return 'black';
    }

    let savedQty = this.data.dailyReports
      .filter((dr) => dr.code == task.CPBillingCode)
      .map((filteredDr) => filteredDr.qty)
      .reduce((partSum, a) => partSum + a, 0);

    if (!isNaN(currentQty)) savedQty += currentQty;

    if (task.planQty > savedQty) return 'black';

    return 'crimson';
  }

  highlightedDates = (isoString) => {
    const date = new Date(isoString);
    const utcDay = date.getUTCDate();

    if (utcDay % 5 === 0) {
      return {
        textColor: '#800080',
        backgroundColor: '#ffc0cb',
      };
    }

    if (utcDay % 3 === 0) {
      return {
        textColor: 'var(--ion-color-secondary-contrast)',
        backgroundColor: 'var(--ion-color-secondary)',
      };
    }

    return undefined;
  };
}
